import { useState, useEffect } from "react";
import { ArrowLeft, MessageCircle, Mail, Send, Phone, FileText, X, Plus, Paperclip, Bold, Italic, List, AlignLeft, Eye, Globe, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { config } from "@/src/config";
import { CallButton } from "./CallButton";
import { CallHistory } from "./CallHistory";
import { PhoneInterface } from "./PhoneInterface";
import { formatDateOnly } from "@/utils/dateTime";
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

interface Provider {
  id?: string;
  provider_id?: string;
  organization_name?: string;
  organization_type?: string;
  primary_phone?: string;
  primary_email?: string;
  website?: string;
  web_form_url?: string;
  fax_number?: string;
}

interface Communication {
  id: number;
  referral_id: string;
  provider_id: string;
  provider_name: string;
  method_type: 'sms' | 'email' | 'fax' | 'voice' | 'web_form';
  last_message: string;
  last_message_at: string;
  unread_count: number;
  status: 'active' | 'closed';
}

interface Message {
  id: number;
  message_body?: string;
  subject?: string;
  body_text?: string;
  direction: 'inbound' | 'outbound';
  status: string;
  created_at: string;
  delivered_at?: string;
  opened_at?: string;
  sent_at?: string;
  contact_value?: string;
  error_message?: string;
  message_content?: string;
  success?: boolean;

  // PHI Integration fields
  phi_included?: boolean;           // Indicates PHI content
  encryption_status?: string;       // 'encrypted', 'none'
  encrypted_at?: string;            // Encryption timestamp
  is_acceptance_email?: boolean;    // From acceptance workflow
}

interface EmailTemplate {
  id: number;
  name: string;
  description: string | null;
  template_type: string;
  communication_method: string;
  subject: string | null;
  body: string;
  variable_fields: string[];
  is_active: boolean;
}

interface ProviderCommunicationsProps {
  provider: Provider;
  onBack: () => void;
}

export function ProviderCommunications({ provider, onBack }: ProviderCommunicationsProps) {
  const { fmt } = useFormattedDate();
  const tabbed = useTabbedChrome();
  const [activeMethod, setActiveMethod] = useState<'sms' | 'email' | 'web_form' | 'phone'>('sms');
  const [conversations, setConversations] = useState<Communication[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Communication | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);

  // Hierarchical email view state
  const [expandedReferralId, setExpandedReferralId] = useState<string | null>(null);
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const [emailsByReferral, setEmailsByReferral] = useState<Map<string, Message[]>>(new Map());

  // Referrals for this provider
  const [referrals, setReferrals] = useState<any[]>([]);
  const [selectedReferral, setSelectedReferral] = useState<any | null>(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  // Compose area state
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeMessage, setComposeMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [sending, setSending] = useState(false);

  // Web form state
  const [submittingWebForm, setSubmittingWebForm] = useState(false);
  const [webFormResult, setWebFormResult] = useState<any>(null);
  const [screenshotHistory, setScreenshotHistory] = useState<any[]>([]);
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);

  // Phone state
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');

  useEffect(() => {
    fetchProviderReferrals();
    fetchConversations();
    if (activeMethod === 'email') {
      fetchTemplates();
    }
  }, [provider, activeMethod]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
    }
  }, [selectedConversation]);

  // Auto-fetch screenshot history when a referral is selected
  useEffect(() => {
    if (selectedReferral && activeMethod === 'web_form') {
      fetchScreenshotHistory(selectedReferral.id);
    }
  }, [selectedReferral, activeMethod]);

  const fetchProviderReferrals = async () => {
    try {
      setLoadingReferrals(true);
      const providerId = provider.provider_id || provider.id;

      console.log('🔍 Fetching referrals for provider:', providerId);

      const response = await authFetch(`${config.weaveinApi.baseUrl}/api/providers/${providerId}/referrals`);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Received referrals:', data.referrals?.length || 0);
        setReferrals(data.referrals || []);
        // Auto-select first referral if available
        if (data.referrals && data.referrals.length > 0) {
          setSelectedReferral(data.referrals[0]);
        }
      } else {
        console.error('❌ Failed to fetch referrals:', response.status);
      }
    } catch (err) {
      console.error('❌ Error fetching provider referrals:', err);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const providerId = provider.provider_id || provider.id;

      const response = await authFetch(`${config.api.baseUrl}/api/communications?provider_id=${providerId}&method=${activeMethod}`);

      if (response.ok) {
        const data = await response.json();
        const communications = data.communications || [];

        // Filter by active method to ensure we only show correct conversations
        const filteredCommunications = communications.filter((comm: Communication) =>
          comm.method_type === activeMethod
        );

        setConversations(filteredCommunications);

        // Auto-select first conversation only if there are conversations
        if (filteredCommunications.length > 0) {
          setSelectedConversation(filteredCommunications[0]);
        } else {
          // Clear selection if no conversations for this method
          setSelectedConversation(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract subject from email content (if present)
  const extractSubjectFromContent = (content: string): string | undefined => {
    if (!content) return undefined;

    // Try to extract subject line from content if it follows a pattern
    // This is a simple heuristic - adjust based on actual email format
    const subjectMatch = content.match(/Subject:\s*(.+?)(?:\n|$)/i);
    return subjectMatch ? subjectMatch[1].trim() : undefined;
  };

  const fetchMessages = async () => {
    if (!selectedConversation) return;

    try {

      // For email, fetch from referral_communications table (where emails are actually stored)
      // For SMS, fetch from sms/conversations endpoint
      const endpoint = activeMethod === 'sms'
        ? '/api/sms/conversations'
        : '/api/referral-communications';

      const response = await authFetch(`${config.api.baseUrl}${endpoint}?referral_id=${selectedConversation.referral_id}&provider_id=${selectedConversation.provider_id}`);

      if (response.ok) {
        const data = await response.json();

        // For email from referral_communications, filter to only email method_type
        if (activeMethod === 'email') {
          const emailMessages = Array.isArray(data) ? data : [];
          const filteredEmails = emailMessages.filter((msg: any) => msg.method_type === 'email');
          console.log('📧 Fetched email messages:', filteredEmails.length);

          // Transform referral_communications data to match expected message format
          const transformedMessages = filteredEmails.map((msg: any) => ({
            id: msg.id,
            direction: 'outbound' as const, // All emails from referral_communications are outbound
            status: msg.status,
            subject: extractSubjectFromContent(msg.message_content),
            body_text: msg.message_content,
            message_content: msg.message_content,
            created_at: msg.sent_at || msg.created_at,
            sent_at: msg.sent_at,
            delivered_at: msg.delivered_at,
            opened_at: msg.opened_at,
            contact_value: msg.contact_value,
            success: msg.success,
            error_message: msg.error_message
          }));

          setMessages(transformedMessages as any);

          // For hierarchical view: Group emails by referral_id
          if (activeMethod === 'email') {
            const grouped = new Map<string, Message[]>();
            transformedMessages.forEach((msg: any) => {
              const refId = selectedConversation.referral_id;
              if (!grouped.has(refId)) {
                grouped.set(refId, []);
              }
              grouped.get(refId)!.push(msg);
            });
            setEmailsByReferral(grouped);
          }
        } else {
          // For SMS, use the standard messages or emails field
          setMessages(data.messages || data.emails || []);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  // Toggle referral expansion in hierarchical view
  const toggleReferralExpansion = (referralId: string) => {
    setExpandedReferralId(expandedReferralId === referralId ? null : referralId);
    setSelectedEmailId(null); // Clear selected email when toggling
  };

  // Select specific email for full detail view
  const selectEmail = (emailId: number) => {
    setSelectedEmailId(selectedEmailId === emailId ? null : emailId);
  };

  // Populate email template with actual referral data
  // For internal view - shows full details since provider is authorized and logged in
  const populateEmailTemplate = (template: string, referralData: any): string => {
    if (!referralData || !template) return template;

    // Replace template variables with actual data
    let populated = template;

    // Provider information
    populated = populated.replace(/{provider_name}/g, provider.organization_name || 'Provider');

    // Member information - show actual data for logged-in provider
    const fullName = `${referralData.first_name || ''} ${referralData.last_name || ''}`.trim();
    populated = populated.replace(/{member_name}/g, fullName || 'Member');
    populated = populated.replace(/{member_medicaid_id}/g, referralData.medicaid_id || 'N/A');
    populated = populated.replace(/{member_dob}/g, formatDateOnly(referralData.dob) !== '-' ? formatDateOnly(referralData.dob) : 'N/A');
    populated = populated.replace(/{member_phone}/g, referralData.phone || 'N/A');

    // Calculate age if DOB is available
    if (referralData.dob) {
      const birthDate = new Date(referralData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      populated = populated.replace(/{member_age}/g, String(age));
    } else {
      populated = populated.replace(/{member_age}/g, 'N/A');
    }

    // Referral information
    populated = populated.replace(/{referral_id}/g, referralData.id || selectedConversation?.referral_id || 'N/A');
    populated = populated.replace(/{primary_diagnosis}/g, referralData.primary_diagnosis || 'N/A');
    populated = populated.replace(/{sdoh_needs}/g, referralData.sdoh_needs || 'N/A');
    populated = populated.replace(/{urgency_level}/g, referralData.urgency_level || 'Normal');
    populated = populated.replace(/{created_at}/g, referralData.created_at ? fmt.short(referralData.created_at) : 'N/A');

    return populated;
  };

  const fetchTemplates = async () => {
    try {
      const params = new URLSearchParams({
        communication_method: 'email',
        is_active: 'true'
      });
      const response = await authFetch(`${config.weaveinApi.baseUrl}/api/communication-templates?${params}`);

      if (response.ok) {
        const data = await response.json();
        setTemplates(data || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleTemplateSelect = (templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    if (template) {
      setSelectedTemplate(templateName);
      setComposeSubject(template.subject || '');
      setComposeMessage(template.body);
    }
  };

  const handleSendMessage = async () => {
    console.log('📤 handleSendMessage called', { activeMethod, provider, selectedReferral });

    const contactValue = activeMethod === 'sms' ? provider.primary_phone : provider.primary_email;
    console.log('📧 Contact value:', contactValue);

    if (!contactValue) {
      console.error('❌ No contact value');
      alert(`Provider ${activeMethod === 'sms' ? 'phone number' : 'email'} not available`);
      return;
    }

    if (activeMethod === 'email' && !composeSubject.trim()) {
      console.error('❌ No subject');
      alert('Please enter a subject');
      return;
    }

    if (!selectedReferral) {
      console.error('❌ No referral selected');
      alert('Please select a referral to discuss');
      return;
    }

    if (!composeMessage.trim()) {
      console.error('❌ No message');
      alert('Please enter a message');
      return;
    }

    console.log('✅ All validations passed, sending...');
    setSending(true);
    try {
      const payload = {
        referral_id: selectedReferral.id,
        provider_id: provider.provider_id || provider.id,
        provider_name: provider.organization_name,
        method_type: activeMethod,
        contact_value: contactValue,
        message_content: composeMessage,
        subject: activeMethod === 'email' ? composeSubject : undefined,
        template_id: selectedTemplate || undefined
      };

      console.log('📨 Sending POST to /api/referral-communications', payload);

      const response = await authFetch(`${config.api.baseUrl}/api/referral-communications`, {
        method: 'POST',
        jsonBody: payload
      });

      console.log('📬 Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Send successful:', result);
        alert('Message sent successfully!');
        setComposeSubject('');
        setComposeMessage('');
        setSelectedTemplate('');
        setShowCompose(false);
        await fetchConversations();
        await fetchMessages();
      } else {
        const error = await response.json();
        console.error('❌ Send failed:', error);
        alert(`Failed to send: ${error.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const fetchScreenshotHistory = async (referralId: string) => {
    setLoadingScreenshots(true);
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/referrals/${referralId}/screenshots`);

      if (response.ok) {
        const data = await response.json();
        setScreenshotHistory(data.screenshots || []);
      } else {
        console.error('Failed to fetch screenshot history');
        setScreenshotHistory([]);
      }
    } catch (error) {
      console.error('Error fetching screenshot history:', error);
      setScreenshotHistory([]);
    } finally {
      setLoadingScreenshots(false);
    }
  };

  const handleSubmitWebForm = async () => {
    if (!selectedReferral) {
      alert('Please select a referral first');
      return;
    }

    setSubmittingWebForm(true);
    setWebFormResult(null);

    try {
      const providerId = provider.provider_id || provider.id;

      console.log(`🌐 Submitting web form for provider ${providerId}, referral ${selectedReferral.id}`);

      const response = await authFetch(`${config.weaveinApi.baseUrl}/api/providers/${providerId}/communications/webform`, {
        method: 'POST',
        jsonBody: {
            referral_id: selectedReferral.id
          }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setWebFormResult(result);
        alert(result.form_submitted ? 'Form submitted successfully!' : 'Form fields filled successfully!');
        await fetchConversations();
        await fetchMessages();
        // Fetch updated screenshot history
        await fetchScreenshotHistory(selectedReferral.id);
      } else {
        setWebFormResult(result);
        alert(`Web form submission failed: ${result.error || result.message}`);
      }
    } catch (error) {
      console.error('Error submitting web form:', error);
      alert('Failed to submit web form');
      setWebFormResult({ success: false, error: String(error) });
    } finally {
      setSubmittingWebForm(false);
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'sms': return <MessageCircle className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'fax': return <FileText className="w-4 h-4" />;
      case 'voice': return <Phone className="w-4 h-4" />;
      case 'web_form': return <Globe className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3 md:space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            </button>
            <div>
              {!tabbed && (
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Communications
              </h1>
              )}
              <p className="text-sm md:text-base text-gray-600 mt-1">
                {provider.organization_name} • {provider.organization_type}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Communication Method Selector - Horizontal Tabs */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => setActiveMethod('sms')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeMethod === 'sms'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              SMS
            </button>
            <button
              onClick={() => setActiveMethod('email')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeMethod === 'email'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Mail className="w-5 h-5" />
              Email
            </button>
            <button
              onClick={() => setActiveMethod('web_form')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeMethod === 'web_form'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Globe className="w-5 h-5" />
              Web Form
            </button>
            <button
              onClick={() => setActiveMethod('phone')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeMethod === 'phone'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Phone className="w-5 h-5" />
              Phone
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-full mx-auto px-4">
        {activeMethod === 'phone' ? (
          /* Phone Tab - Unified Phone Interface */
          <PhoneInterface
            provider={provider}
            referrals={referrals}
          />
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {/* Left Sidebar - Conversations List */}
            <div className="w-full">
              <Card className="border-2 border-blue-200/50 shadow-xl bg-white rounded-2xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200/40">
                  <CardTitle className="text-lg font-semibold">Conversations</CardTitle>
                </CardHeader>

              <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : conversations.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p className="mb-2">No conversations yet</p>
                    <Button
                      onClick={() => setShowCompose(true)}
                      size="sm"
                      className="bg-v2accent hover:bg-v2accent/90 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Start New
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => {
                          console.log(`📌 Conversation clicked: ${conv.referral_id} (${conv.method_type})`);
                          setSelectedConversation(conv);
                          // For web form conversations, auto-show compose view with the referral
                          if (conv.method_type === 'web_form') {
                            setShowCompose(true);
                            const referral = referrals.find(r => r.id === conv.referral_id);
                            if (referral) {
                              setSelectedReferral(referral);
                            }
                          } else {
                            // For non-web-form conversations, close compose mode
                            setShowCompose(false);
                          }
                        }}
                        className={`w-full p-4 text-left transition-colors relative ${
                          (() => {
                            const isSelected = (selectedConversation?.id === conv.id) ||
                              (showCompose && selectedReferral?.id === conv.referral_id);
                            if (isSelected) {
                              console.log(`✅ Highlighted: ${conv.referral_id}`);
                            }
                            return isSelected
                              ? 'bg-blue-50 border-l-4 border-blue-600'
                              : 'hover:bg-gray-50 border-l-4 border-transparent';
                          })()
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getMethodIcon(conv.method_type)}
                            <span className="font-medium text-gray-900">Referral #{conv.referral_id}</span>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-red-500 text-white text-xs">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate">{conv.last_message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {fmt.dateTime(conv.last_message_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Messages & Compose */}
          <div className="w-full">
            <Card className="border-2 border-blue-200/50 shadow-xl bg-white rounded-2xl overflow-hidden min-h-[700px] flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200/40">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    {showCompose && selectedReferral
                      ? `Referral #${selectedReferral.id}`
                      : selectedConversation
                        ? `Referral #${selectedConversation.referral_id}`
                        : 'Select a conversation'}
                  </CardTitle>
                  {selectedConversation && (
                    <Button
                      onClick={() => setShowCompose(!showCompose)}
                      size="sm"
                      variant={showCompose ? "outline" : "default"}
                      className={showCompose ? "" : "bg-v2accent hover:bg-v2accent/90 text-white"}
                    >
                      {showCompose ? (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Compose
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
                {showCompose ? (
                  /* Compose Area */
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      {activeMethod === 'web_form' ? <Globe className="w-5 h-5 text-blue-600" /> : <Send className="w-5 h-5 text-blue-600" />}
                      {activeMethod === 'sms' ? 'Compose SMS' : activeMethod === 'email' ? 'Compose Email' : 'Submit via Web Form'}
                    </h3>

                    {/* Referral Selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Referral to Discuss <span className="text-red-600">*</span>
                      </label>
                      {loadingReferrals ? (
                        <div className="text-sm text-gray-500">Loading referrals...</div>
                      ) : referrals.length === 0 ? (
                        <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                          ⚠️ No referrals matched to this provider yet. Match a referral first.
                        </div>
                      ) : (
                        <>
                          <select
                            value={selectedReferral?.id ? String(selectedReferral.id) : ''}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              console.log('🔍 Dropdown changed:', selectedValue);

                              // Find referral by comparing as strings
                              const referral = referrals.find(r => String(r.id) === selectedValue);
                              console.log('✅ Found referral:', referral);

                              setSelectedReferral(referral || null);

                              // Also update selectedConversation to match this referral
                              // This ensures the blue highlight bar appears on the correct conversation
                              if (referral) {
                                const matchingConv = conversations.find(c => String(c.referral_id) === String(referral.id));
                                if (matchingConv) {
                                  setSelectedConversation(matchingConv);
                                }
                              }
                            }}
                            className="w-full border-2 border-blue-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                          >
                            <option value="">-- Select a referral --</option>
                            {referrals.map(r => (
                              <option key={r.id} value={String(r.id)}>
                                {r.first_name} {r.last_name} - {(r.sdoh_needs || []).join(', ')} ({r.status})
                              </option>
                            ))}
                          </select>
                          {selectedReferral && (
                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-1">
                              <div className="font-semibold text-blue-900">
                                {selectedReferral.first_name} {selectedReferral.last_name}
                              </div>
                              <div className="text-blue-700">
                                <strong>Needs:</strong> {(selectedReferral.sdoh_needs || []).join(', ')}
                              </div>
                              <div className="text-blue-600">
                                <strong>Urgency:</strong> {selectedReferral.urgency_level || 'N/A'} |{' '}
                                <strong>Status:</strong> {selectedReferral.status}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {activeMethod === 'web_form' ? (
                      /* Web Form Submission UI */
                      <>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            Automatic Web Form Submission
                          </h4>
                          <p className="text-sm text-blue-700">
                            This will automatically fill and submit the referral on the provider's website.
                            The system will capture screenshots for verification.
                          </p>
                        </div>

                        {!provider.website && !provider.web_form_url ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <p className="text-sm text-amber-800">
                              ⚠️ Provider website URL not available. Please add a website URL to use this feature.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                              <p className="text-sm"><strong>Provider:</strong> {provider.organization_name}</p>
                              <p className="text-sm"><strong>Website:</strong> {provider.web_form_url || provider.website}</p>
                              {selectedReferral && (
                                <>
                                  <p className="text-sm"><strong>Referral:</strong> {selectedReferral.id}</p>
                                  <p className="text-sm"><strong>Member:</strong> {selectedReferral.first_name} {selectedReferral.last_name}</p>
                                  <p className="text-sm"><strong>Services:</strong> {selectedReferral.sdoh_needs || 'Not specified'}</p>
                                </>
                              )}
                            </div>

                            {webFormResult && (
                              <div className={`p-4 rounded-lg border ${
                                webFormResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                              }`}>
                                <p className={`font-semibold ${
                                  webFormResult.success ? 'text-green-900' : 'text-red-900'
                                }`}>
                                  {webFormResult.message || (webFormResult.success ? 'Success!' : 'Failed')}
                                </p>

                                {/* Fields Filled Summary */}
                                {webFormResult.fields_filled && (
                                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Form Fields Filled:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(webFormResult.fields_filled).map(([field, filled]) => (
                                        <span
                                          key={field}
                                          className={`px-2 py-1 rounded text-xs font-medium ${
                                            filled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                          }`}
                                        >
                                          {field}: {filled ? '✓' : '✗'}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Page Info */}
                                {webFormResult.page_url && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    <strong>Page:</strong> {webFormResult.page_title || webFormResult.page_url}
                                  </p>
                                )}

                                {/* Screenshots */}
                                {(webFormResult.screenshot_before || webFormResult.screenshot_after) && (
                                  <div className="mt-4">
                                    <p className="text-sm font-semibold text-gray-700 mb-2">Screenshots:</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {webFormResult.screenshot_before && (
                                        <div>
                                          <p className="text-xs text-gray-600 mb-1">Before Filling</p>
                                          <img
                                            src={`data:image/png;base64,${webFormResult.screenshot_before}`}
                                            alt="Before filling form"
                                            className="w-full border border-gray-300 rounded shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                                            onClick={() => window.open(`data:image/png;base64,${webFormResult.screenshot_before}`, '_blank')}
                                          />
                                        </div>
                                      )}
                                      {webFormResult.screenshot_after && (
                                        <div>
                                          <p className="text-xs text-gray-600 mb-1">After Filling</p>
                                          <img
                                            src={`data:image/png;base64,${webFormResult.screenshot_after}`}
                                            alt="After filling form"
                                            className="w-full border border-gray-300 rounded shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                                            onClick={() => window.open(`data:image/png;base64,${webFormResult.screenshot_after}`, '_blank')}
                                          />
                                        </div>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2 italic">Click screenshots to view full size in new tab</p>
                                  </div>
                                )}

                                {webFormResult.error && (
                                  <p className="text-sm text-red-700 mt-2">
                                    Error: {webFormResult.error}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Screenshot History */}
                            {selectedReferral && screenshotHistory.length > 0 && (
                              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <h3 className="text-md font-semibold text-gray-800 mb-3">
                                  📸 Previous Submissions ({screenshotHistory.length})
                                </h3>
                                <div className="space-y-4 max-h-96 overflow-y-auto">
                                  {screenshotHistory.map((screenshot, index) => (
                                    <div key={screenshot.id} className="p-3 bg-white rounded border border-gray-200">
                                      <div className="flex justify-between items-start mb-2">
                                        <div>
                                          <p className="text-sm font-medium text-gray-700">
                                            Submission #{screenshotHistory.length - index}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {fmt.dateTime(screenshot.created_at)}
                                          </p>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          screenshot.submission_success
                                            ? 'bg-green-100 text-green-800'
                                            : screenshot.form_submitted
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-gray-100 text-gray-600'
                                        }`}>
                                          {screenshot.submission_success ? 'Submitted' : screenshot.form_submitted ? 'Attempted' : 'Filled'}
                                        </span>
                                      </div>

                                      {/* Fields Summary */}
                                      {screenshot.fields_filled && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                          {Object.entries(screenshot.fields_filled).map(([field, filled]) => (
                                            filled && (
                                              <span key={field} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                                {field}
                                              </span>
                                            )
                                          ))}
                                        </div>
                                      )}

                                      {/* Screenshots */}
                                      <div className="grid grid-cols-2 gap-2">
                                        {screenshot.screenshot_before && (
                                          <div>
                                            <p className="text-xs text-gray-500 mb-1">Before</p>
                                            <img
                                              src={`data:image/png;base64,${screenshot.screenshot_before}`}
                                              alt="Before"
                                              className="w-full border border-gray-200 rounded cursor-pointer hover:opacity-80"
                                              onClick={() => window.open(`data:image/png;base64,${screenshot.screenshot_before}`, '_blank')}
                                            />
                                          </div>
                                        )}
                                        {screenshot.screenshot_after && (
                                          <div>
                                            <p className="text-xs text-gray-500 mb-1">After</p>
                                            <img
                                              src={`data:image/png;base64,${screenshot.screenshot_after}`}
                                              alt="After"
                                              className="w-full border border-gray-200 rounded cursor-pointer hover:opacity-80"
                                              onClick={() => window.open(`data:image/png;base64,${screenshot.screenshot_after}`, '_blank')}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Load Screenshot History Button */}
                            {selectedReferral && screenshotHistory.length === 0 && !loadingScreenshots && (
                              <div className="mt-4">
                                <Button
                                  onClick={() => fetchScreenshotHistory(selectedReferral.id)}
                                  variant="outline"
                                  className="w-full"
                                >
                                  📸 Load Previous Submissions
                                </Button>
                              </div>
                            )}
                          </>
                        )}

                        {/* Web Form Submit Button */}
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => {
                              setShowCompose(false);
                              setWebFormResult(null);
                            }}
                            variant="outline"
                            disabled={submittingWebForm}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSubmitWebForm}
                            disabled={submittingWebForm || !selectedReferral || (!provider.website && !provider.web_form_url)}
                            className="bg-v2accent hover:bg-v2accent/90 text-white font-semibold px-6"
                          >
                            {submittingWebForm ? (
                              <>Submitting...</>
                            ) : (
                              <>
                                <Globe className="w-4 h-4 mr-2" />
                                Submit to Provider Website
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    ) : (
                      /* Email/SMS Compose UI */
                      <>
                        {activeMethod === 'email' && (
                          <>
                            {/* Template Selector */}
                            {templates.length > 0 && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Use Template
                                </label>
                                <select
                                  value={selectedTemplate}
                                  onChange={(e) => handleTemplateSelect(e.target.value)}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                >
                                  <option value="">-- Select Template --</option>
                                  {templates.map(t => (
                                    <option key={t.id} value={t.name}>
                                      {t.name} {t.description ? `- ${t.description}` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* Subject */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Subject
                              </label>
                              <Input
                                value={composeSubject}
                                onChange={(e) => setComposeSubject(e.target.value)}
                                placeholder="Email subject..."
                                className="text-sm"
                              />
                            </div>
                          </>
                        )}

                        {/* Message Body */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Message {selectedTemplate && <span className="text-blue-600 text-xs ml-2">(from template: {selectedTemplate})</span>}
                            </label>
                            {activeMethod === 'sms' && (
                              <span className={`text-xs font-medium ${composeMessage.length > 160 ? 'text-orange-600' : 'text-gray-500'}`}>
                                {composeMessage.length} characters {composeMessage.length > 160 && `(~${Math.ceil(composeMessage.length / 160)} SMS segments)`}
                              </span>
                            )}
                            {activeMethod === 'email' && composeMessage.length > 0 && (
                              <span className="text-xs text-gray-500">
                                {composeMessage.length} characters
                              </span>
                            )}
                          </div>
                          <Textarea
                            value={composeMessage}
                            onChange={(e) => setComposeMessage(e.target.value)}
                            placeholder={activeMethod === 'sms' ? "Type your SMS message..." : "Type your email message..."}
                            className="min-h-[200px] max-h-[600px] text-sm resize-y"
                            rows={8}
                          />
                          <p className="text-xs text-gray-400 mt-1 italic">
                            Tip: Drag the bottom-right corner to resize the message box
                          </p>
                        </div>

                        {/* PHI Disclosure Notice (for email) */}
                        {activeMethod === 'email' && (
                          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                            <div className="flex items-start gap-3">
                              <Shield className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-purple-900 mb-1">🔒 Secure PHI Email</p>
                                <p className="text-xs text-purple-800 leading-relaxed">
                                  This email will be sent via encrypted TLS connection and logged in PHI access audit trail per HIPAA requirements.
                                  All member personal and health information is Protected Health Information (PHI).
                                </p>
                                <div className="mt-2 text-xs text-purple-700">
                                  <p className="font-medium mb-1">Security measures:</p>
                                  <ul className="space-y-0.5 ml-4">
                                    <li>• Encrypted transmission (TLS 1.2+)</li>
                                    <li>• PHI access logging</li>
                                    <li>• HIPAA-compliant storage</li>
                                    <li>• Audit trail maintained</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Send Button */}
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => {
                              setShowCompose(false);
                              setComposeSubject('');
                              setComposeMessage('');
                              setSelectedTemplate('');
                            }}
                            variant="outline"
                            disabled={sending}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSendMessage}
                            disabled={sending}
                            className="bg-v2accent hover:bg-v2accent/90 text-white font-semibold px-6"
                          >
                            {sending ? (
                              <>Sending...</>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send {activeMethod === 'sms' ? 'SMS' : 'Email'}
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  /* Messages Display */
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {!selectedConversation ? (
                      <div className="text-center py-12 text-gray-500">
                        <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Select a conversation to view messages</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>No messages yet</p>
                        <p className="text-sm mt-2">Click "Compose" above to start a conversation</p>
                      </div>
                    ) : activeMethod === 'email' ? (
                      /* HIERARCHICAL EMAIL VIEW */
                      <div className="space-y-4">
                        {/* Level 1: Referral Summary Card */}
                        <div className="bg-white rounded-xl border-2 border-blue-200 shadow-sm overflow-hidden">
                          <button
                            onClick={() => toggleReferralExpansion(selectedConversation.referral_id)}
                            className="w-full p-4 text-left hover:bg-blue-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <Mail className="w-5 h-5 text-blue-600" />
                                  <h3 className="font-semibold text-gray-900">
                                    Referral #{selectedConversation.referral_id}
                                  </h3>
                                  <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                                    {messages.length} {messages.length === 1 ? 'email' : 'emails'}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Last: {messages.length > 0 && fmt.short(messages[messages.length - 1].created_at)}
                                </p>
                              </div>
                              <div className="text-gray-400">
                                {expandedReferralId === selectedConversation.referral_id ? '▼' : '▶'}
                              </div>
                            </div>
                          </button>

                          {/* Level 2: Email List (Expanded) */}
                          {expandedReferralId === selectedConversation.referral_id && (
                            <div className="border-t border-gray-200 bg-gray-50">
                              <div className="p-4 space-y-2">
                                {messages.map((msg, index) => (
                                  <div key={msg.id}>
                                    <button
                                      onClick={() => selectEmail(msg.id)}
                                      className={`w-full text-left p-3 rounded-lg transition-all ${
                                        selectedEmailId === msg.id
                                          ? 'bg-blue-100 border-2 border-blue-400'
                                          : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                              <span>Email #{index + 1}</span>
                                              {/* PHI Indicator */}
                                              {msg.phi_included && (
                                                <Shield className="w-3 h-3 text-purple-600" />
                                              )}
                                            </span>
                                            <Badge
                                              variant="outline"
                                              className={`text-xs ${
                                                msg.status === 'delivered' ? 'bg-green-100 text-green-800 border-green-300' :
                                                msg.status === 'sent' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                                msg.status === 'failed' ? 'bg-red-100 text-red-800 border-red-300' :
                                                'bg-gray-100 text-gray-800 border-gray-300'
                                              }`}
                                            >
                                              {msg.status}
                                            </Badge>
                                          </div>
                                          {msg.subject && (
                                            <p className="font-medium text-sm text-gray-900 truncate">
                                              {msg.subject}
                                            </p>
                                          )}
                                          <p className="text-xs text-gray-500 mt-1 truncate">
                                            {msg.body_text ? msg.body_text.substring(0, 80) + '...' : 'No preview'}
                                          </p>
                                          <p className="text-xs text-gray-400 mt-1">
                                            {fmt.dateTime(msg.created_at)}
                                          </p>
                                        </div>
                                        <div className="text-gray-400 flex-shrink-0">
                                          {selectedEmailId === msg.id ? '▼' : '▶'}
                                        </div>
                                      </div>
                                    </button>

                                    {/* Level 3: Full Email Detail (Expanded) */}
                                    {selectedEmailId === msg.id && (
                                      <div className="mt-2 p-4 bg-white border-2 border-blue-300 rounded-lg">
                                        <div className="flex items-center justify-between mb-3">
                                          <h4 className="font-semibold text-gray-900">Full Email Details</h4>
                                          <button
                                            onClick={() => setSelectedEmailId(null)}
                                            className="text-gray-400 hover:text-gray-600"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>

                                        <div className="space-y-3">
                                          {/* Email metadata */}
                                          <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                              <span className="font-medium text-gray-600">From:</span>
                                              <p className="text-gray-900">Trust Marketplace</p>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">To:</span>
                                              <p className="text-gray-900">{msg.contact_value || provider.primary_email}</p>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">Sent:</span>
                                              <p className="text-gray-900">{fmt.dateTime(msg.sent_at || msg.created_at)}</p>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">Status:</span>
                                              <Badge
                                                className={`${
                                                  msg.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                  msg.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                                                  msg.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                  'bg-gray-100 text-gray-800'
                                                }`}
                                              >
                                                {msg.status}
                                              </Badge>
                                            </div>
                                          </div>

                                          {/* Subject */}
                                          {msg.subject && (
                                            <div>
                                              <span className="font-medium text-gray-600 text-sm">Subject:</span>
                                              <p className="text-gray-900 mt-1">{msg.subject}</p>
                                            </div>
                                          )}

                                          {/* Email body */}
                                          <div>
                                            <span className="font-medium text-gray-600 text-sm">Message:</span>
                                            <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                                {(() => {
                                                  // Find referral data for this email
                                                  const referralData = referrals.find(r => r.id === selectedConversation.referral_id);
                                                  // Populate template with actual data for logged-in provider
                                                  return referralData ? populateEmailTemplate(msg.body_text || '', referralData) : msg.body_text;
                                                })()}
                                              </p>
                                            </div>
                                          </div>

                                          {/* Error message if failed */}
                                          {msg.status === 'failed' && msg.error_message && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                              <span className="font-medium text-red-900 text-sm">Error:</span>
                                              <p className="text-sm text-red-700 mt-1">{msg.error_message}</p>
                                            </div>
                                          )}

                                          {/* Delivery info */}
                                          {(msg.delivered_at || msg.opened_at) && (
                                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                                              {msg.delivered_at && (
                                                <p className="text-green-800">
                                                  ✓ Delivered: {fmt.dateTime(msg.delivered_at)}
                                                </p>
                                              )}
                                              {msg.opened_at && (
                                                <p className="text-green-800">
                                                  ✓ Opened: {fmt.dateTime(msg.opened_at)}
                                                </p>
                                              )}
                                            </div>
                                          )}

                                          {/* PHI Security Notice */}
                                          {msg.phi_included && (
                                            <div className="p-3 bg-purple-50 border-2 border-purple-300 rounded-lg">
                                              <div className="flex items-start gap-2">
                                                <Shield className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1">
                                                  <p className="font-semibold text-purple-900 text-sm">
                                                    Secure PHI Email
                                                  </p>
                                                  <p className="text-xs text-purple-700 mt-1">
                                                    This email contains Protected Health Information and was:
                                                  </p>
                                                  <ul className="text-xs text-purple-700 mt-2 space-y-1 ml-4">
                                                    <li>• Encrypted at rest (AES-256-GCM)</li>
                                                    <li>• Transmitted via TLS 1.2+</li>
                                                    <li>• Logged in PHI access audit</li>
                                                    {msg.encrypted_at && (
                                                      <li>• Encrypted on: {fmt.dateTime(msg.encrypted_at)}</li>
                                                    )}
                                                  </ul>
                                                  {msg.is_acceptance_email && (
                                                    <p className="text-xs text-purple-800 mt-2 font-medium">
                                                      ✓ Sent automatically upon referral acceptance
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* FLAT VIEW FOR SMS/OTHER METHODS */
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-4 rounded-lg ${
                            msg.direction === 'outbound'
                              ? 'bg-blue-100 ml-8'
                              : 'bg-gray-100 mr-8'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-700">
                              {msg.direction === 'outbound' ? 'You' : provider.organization_name}
                            </span>
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                msg.status === 'delivered' ? 'bg-green-100 text-green-800 border-green-300' :
                                msg.status === 'sent' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                msg.status === 'failed' ? 'bg-red-100 text-red-800 border-red-300' :
                                'bg-gray-100 text-gray-800 border-gray-300'
                              }`}
                            >
                              {msg.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {msg.body_text}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            {fmt.dateTime(msg.created_at)}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
